import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  AlertTriangle,
  AlertCircle,
  Link as LinkIcon,
  GitCompare,
  MinusCircle,
  PlusCircle,
  ArrowRight,
  Info,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { embedClauses } from '../../services/api';

// Types
export interface ClauseMatch {
  sourceClauseId: string;
  targetClauseId: string;
  similarity: number;
  matchType: 'exact' | 'similar' | 'partial' | 'different';
}

export interface UnmatchedClause {
  clauseId: string;
  clauseText: string;
  contractId: string;
  contractState: string;
  articleNumber: string;
  sectionNumber: string;
  bestMatchSimilarity: number;
}

export interface Clause {
  id: string;
  number: string;
  text: string;
  hasCfrCitation?: boolean;
  hasRisk?: boolean;
  riskSeverity?: 'critical' | 'high' | 'medium' | 'low';
}

export interface Section {
  id: string;
  number: string;
  title: string;
  clauses: Clause[];
}

export interface Article {
  id: string;
  number: string;
  title: string;
  sections: Section[];
  riskCount?: number;
}

export interface ContractData {
  id: string;
  name: string;
  state: string;
  articles: Article[];
}

export interface EmbeddingStats {
  sourceClausesTotal: number;
  sourceClausesWithEmbeddings: number;
  targetClausesTotal: number;
  targetClausesWithEmbeddings: number;
  warning?: string;
}

export interface SideBySideViewerProps {
  sourceContract: ContractData | null;
  targetContract: ContractData | null;
  clauseMatches?: ClauseMatch[];
  unmatchedClauses?: {
    sourceUnmatched: UnmatchedClause[];
    targetUnmatched: UnmatchedClause[];
    totalSourceUnmatched: number;
    totalTargetUnmatched: number;
  };
  embeddingStats?: EmbeddingStats;
  onClauseClick?: (clause: Clause, section: Section, article: Article, contractId: string) => void;
  onEmbeddingsGenerated?: () => void;
  testId?: string;
}

// Helper to get similarity color
const getSimilarityColor = (similarity: number): string => {
  if (similarity >= 0.9) return 'bg-green-100 border-green-400';
  if (similarity >= 0.7) return 'bg-blue-100 border-blue-400';
  if (similarity >= 0.5) return 'bg-yellow-100 border-yellow-400';
  return 'bg-gray-100 border-gray-300';
};

const getSimilarityBadgeColor = (similarity: number): string => {
  if (similarity >= 0.9) return 'bg-green-500 text-white';
  if (similarity >= 0.7) return 'bg-blue-500 text-white';
  if (similarity >= 0.5) return 'bg-yellow-500 text-white';
  return 'bg-gray-400 text-white';
};

// Helper to detect if an article is a glossary/definitions section
const GLOSSARY_KEYWORDS = [
  'definition', 'definitions', 'glossary', 'terms', 'terminology',
  'acronyms', 'abbreviations', 'defined terms'
];

const isGlossaryArticle = (article: Article): boolean => {
  const titleLower = article.title.toLowerCase();
  return GLOSSARY_KEYWORDS.some(keyword => titleLower.includes(keyword));
};

// Target clause context interface
interface TargetClauseContext {
  clause: Clause;
  section: Section;
  article: Article;
}

type AlignmentLabel = 'Equivalent' | 'Partial' | 'Missing' | 'Conflict' | 'Additional';

interface SectionAlignmentCluster {
  sectionPath: string;
  label: AlignmentLabel;
  score: number;
  matches: Array<{
    sourceClause: Clause;
    targetClause: Clause;
    targetSection: Section;
    targetArticle: Article;
    similarity: number;
    snippet: string;
    page: number;
  }>;
}

// Clause pair row component - shows baseline clause with its matched target clause
interface ClausePairRowProps {
  baselineClause: Clause;
  baselineSection: Section;
  baselineArticle: Article;
  baselineContractId: string;
  matchingTargetClause?: {
    clause: Clause;
    section: Section;
    article: Article;
    similarity: number;
  };
  isUnmatched: boolean;
  unmatchedInfo?: UnmatchedClause;
  onClauseClick?: (clause: Clause, section: Section, article: Article, contractId: string) => void;
  targetContractId?: string;
}

const ClausePairRow: React.FC<ClausePairRowProps> = ({
  baselineClause,
  baselineSection,
  baselineArticle,
  baselineContractId,
  matchingTargetClause,
  isUnmatched,
  unmatchedInfo,
  onClauseClick,
  targetContractId,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const similarity = matchingTargetClause?.similarity || 0;

  // Baseline clause styling
  let baselineClasses: string;
  if (isUnmatched) {
    baselineClasses = 'bg-orange-50 border-2 border-orange-400';
  } else if (matchingTargetClause) {
    baselineClasses = `${getSimilarityColor(similarity)} border-2`;
  } else {
    baselineClasses = 'bg-white border border-gray-200';
  }

  // Target clause styling (or placeholder)
  let targetClasses: string;
  if (isUnmatched || !matchingTargetClause) {
    targetClasses = 'bg-gray-50 border-2 border-dashed border-gray-300';
  } else {
    targetClasses = `${getSimilarityColor(similarity)} border-2`;
  }

  return (
    <div
      className="flex gap-4 mb-3"
      data-testid={`clause-pair-${baselineClause.id}`}
    >
      {/* Baseline (Source) Clause */}
      <div
        className={`flex-1 p-3 rounded-md cursor-pointer transition-all hover:shadow-md relative ${baselineClasses}`}
        onClick={() => onClauseClick?.(baselineClause, baselineSection, baselineArticle, baselineContractId)}
        onMouseEnter={() => (matchingTargetClause || isUnmatched) && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        data-testid={`baseline-clause-${baselineClause.id}`}
        data-clause-id={baselineClause.id}
      >
        {/* Clause number and indicators */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-gray-600">{baselineClause.number}</span>
          {baselineClause.hasCfrCitation && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-primary-100 text-primary-700">
              <LinkIcon className="h-3 w-3 mr-0.5" />
              CFR
            </span>
          )}
          {baselineClause.hasRisk && baselineClause.riskSeverity && (
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
                baselineClause.riskSeverity === 'critical'
                  ? 'bg-danger-100 text-danger-700'
                  : baselineClause.riskSeverity === 'high'
                  ? 'bg-warning-100 text-warning-700'
                  : baselineClause.riskSeverity === 'medium'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-success-100 text-success-700'
              }`}
            >
              <AlertTriangle className="h-3 w-3 mr-0.5" />
              {baselineClause.riskSeverity}
            </span>
          )}
          {isUnmatched && (
            <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500 text-white">
              <AlertCircle className="h-3 w-3 mr-1" />
              No match
            </span>
          )}
          {matchingTargetClause && (
            <span
              className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSimilarityBadgeColor(similarity)}`}
            >
              {Math.round(similarity * 100)}%
            </span>
          )}
        </div>

        {/* Clause text */}
        <p className="text-sm text-gray-700 leading-relaxed">{baselineClause.text}</p>

        {/* Tooltip */}
        {showTooltip && matchingTargetClause && (
          <div className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap">
            <div className="font-medium">Similarity: {Math.round(similarity * 100)}%</div>
            <div className="text-gray-300">
              {similarity >= 0.9 ? 'Nearly identical' : similarity >= 0.7 ? 'Similar content' : similarity >= 0.5 ? 'Partially similar' : 'Different content'}
            </div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>
        )}

        {showTooltip && isUnmatched && unmatchedInfo && (
          <div className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap">
            <div className="font-medium">No equivalent found</div>
            <div className="text-gray-300">Best match: {Math.round(unmatchedInfo.bestMatchSimilarity * 100)}%</div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>
        )}
      </div>

      {/* Match indicator arrow */}
      <div className="flex items-center justify-center w-8 flex-shrink-0">
        {matchingTargetClause ? (
          <ArrowRight className="h-5 w-5 text-gray-400" />
        ) : (
          <div className="h-5 w-5" /> /* Placeholder */
        )}
      </div>

      {/* Target (Matched) Clause or Placeholder */}
      <div
        className={`flex-1 p-3 rounded-md transition-all ${targetClasses} ${matchingTargetClause ? 'cursor-pointer hover:shadow-md' : ''}`}
        onClick={() => matchingTargetClause && targetContractId && onClauseClick?.(
          matchingTargetClause.clause,
          matchingTargetClause.section,
          matchingTargetClause.article,
          targetContractId
        )}
        data-testid={`target-clause-${matchingTargetClause?.clause.id || 'placeholder'}`}
      >
        {matchingTargetClause ? (
          <>
            {/* Show where this target clause comes from */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold text-gray-600">{matchingTargetClause.clause.number}</span>
              <span className="text-xs text-gray-500 italic">
                From: Article {matchingTargetClause.article.number} - {matchingTargetClause.section.number}
              </span>
              {matchingTargetClause.clause.hasCfrCitation && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-primary-100 text-primary-700">
                  <LinkIcon className="h-3 w-3 mr-0.5" />
                  CFR
                </span>
              )}
              <span className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSimilarityBadgeColor(similarity)}`}>
                {Math.round(similarity * 100)}%
              </span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{matchingTargetClause.clause.text}</p>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full min-h-[60px] text-gray-400">
            <AlertCircle className="h-5 w-5 mb-1" />
            <span className="text-xs">No matching clause</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Section group component for baseline-centric view
interface SectionGroupProps {
  section: Section;
  article: Article;
  sourceContractId: string;
  targetContractId?: string;
  sourceToTargetMatch: Map<string, { targetClauseId: string; similarity: number }>;
  targetClauseContextMap: Map<string, TargetClauseContext>;
  unmatchedClauseIds: Set<string>;
  unmatchedClausesMap: Map<string, UnmatchedClause>;
  isExpanded: boolean;
  onToggle: () => void;
  onClauseClick?: (clause: Clause, section: Section, article: Article, contractId: string) => void;
}

const SectionGroup: React.FC<SectionGroupProps> = ({
  section,
  article,
  sourceContractId,
  targetContractId,
  sourceToTargetMatch,
  targetClauseContextMap,
  unmatchedClauseIds,
  unmatchedClausesMap,
  isExpanded,
  onToggle,
  onClauseClick,
}) => {
  // Count matched clauses in this section
  const matchedCount = section.clauses.filter(c => sourceToTargetMatch.has(c.id)).length;
  const unmatchedCount = section.clauses.filter(c => unmatchedClauseIds.has(c.id)).length;

  return (
    <div className="border-l-2 border-gray-200 pl-4 ml-2 mb-3" data-testid={`section-${section.id}`}>
      <button
        onClick={onToggle}
        className="flex items-center w-full text-left py-2 hover:bg-gray-50 rounded transition-colors"
        data-testid={`section-toggle-${section.id}`}
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
        )}
        <span className="font-medium text-sm text-gray-900">{section.number}</span>
        <span className="ml-2 text-sm text-gray-600">{section.title}</span>
        <div className="ml-auto flex items-center gap-2">
          {matchedCount > 0 && (
            <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
              {matchedCount} matched
            </span>
          )}
          {unmatchedCount > 0 && (
            <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
              {unmatchedCount} unmatched
            </span>
          )}
          <span className="text-xs text-gray-400">{section.clauses.length} clauses</span>
        </div>
      </button>

      {isExpanded && (
        <div className="mt-2" data-testid={`section-clauses-${section.id}`}>
          {section.clauses.map((clause) => {
            const match = sourceToTargetMatch.get(clause.id);
            const targetContext = match ? targetClauseContextMap.get(match.targetClauseId) : undefined;
            const isUnmatched = unmatchedClauseIds.has(clause.id);

            return (
              <ClausePairRow
                key={clause.id}
                baselineClause={clause}
                baselineSection={section}
                baselineArticle={article}
                baselineContractId={sourceContractId}
                matchingTargetClause={targetContext && match ? {
                  ...targetContext,
                  similarity: match.similarity,
                } : undefined}
                isUnmatched={isUnmatched}
                unmatchedInfo={unmatchedClausesMap.get(clause.id)}
                onClauseClick={onClauseClick}
                targetContractId={targetContractId}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// Article group component for baseline-centric view
interface ArticleGroupProps {
  article: Article;
  sourceContractId: string;
  targetContractId?: string;
  sourceToTargetMatch: Map<string, { targetClauseId: string; similarity: number }>;
  targetClauseContextMap: Map<string, TargetClauseContext>;
  unmatchedClauseIds: Set<string>;
  unmatchedClausesMap: Map<string, UnmatchedClause>;
  isExpanded: boolean;
  expandedSections: Set<string>;
  onToggle: () => void;
  onSectionToggle: (sectionId: string) => void;
  onClauseClick?: (clause: Clause, section: Section, article: Article, contractId: string) => void;
}

const ArticleGroup: React.FC<ArticleGroupProps> = ({
  article,
  sourceContractId,
  targetContractId,
  sourceToTargetMatch,
  targetClauseContextMap,
  unmatchedClauseIds,
  unmatchedClausesMap,
  isExpanded,
  expandedSections,
  onToggle,
  onSectionToggle,
  onClauseClick,
}) => {
  // Count matched and unmatched clauses in this article
  const allClauseIds = article.sections.flatMap(s => s.clauses.map(c => c.id));
  const matchedCount = allClauseIds.filter(id => sourceToTargetMatch.has(id)).length;
  const unmatchedCount = allClauseIds.filter(id => unmatchedClauseIds.has(id)).length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-4" data-testid={`article-${article.id}`}>
      <button
        onClick={onToggle}
        className="flex items-center w-full text-left p-4 hover:bg-gray-50 rounded-t-lg transition-colors"
        data-testid={`article-toggle-${article.id}`}
      >
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">Article {article.number}</span>
            {article.riskCount && article.riskCount > 0 && (
              <span className="px-2 py-0.5 bg-danger-100 text-danger-700 rounded-full text-xs">
                {article.riskCount} risks
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 truncate">{article.title}</p>
        </div>
        <div className="flex items-center gap-2 ml-2">
          {matchedCount > 0 && (
            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
              {matchedCount} matched
            </span>
          )}
          {unmatchedCount > 0 && (
            <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">
              {unmatchedCount} unmatched
            </span>
          )}
          <span className="text-xs text-gray-400">{article.sections.length} sections</span>
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4" data-testid={`article-sections-${article.id}`}>
          {article.sections.map((section) => (
            <SectionGroup
              key={section.id}
              section={section}
              article={article}
              sourceContractId={sourceContractId}
              targetContractId={targetContractId}
              sourceToTargetMatch={sourceToTargetMatch}
              targetClauseContextMap={targetClauseContextMap}
              unmatchedClauseIds={unmatchedClauseIds}
              unmatchedClausesMap={unmatchedClausesMap}
              isExpanded={expandedSections.has(section.id)}
              onToggle={() => onSectionToggle(section.id)}
              onClauseClick={onClauseClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Collapsible panel for unmatched clauses (target-only clauses)
interface UnmatchedClausesPanelProps {
  title: string;
  clauses: UnmatchedClause[];
  isSource: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  testId: string;
}

const UnmatchedClausesPanel: React.FC<UnmatchedClausesPanelProps> = ({
  title,
  clauses,
  isSource,
  isExpanded,
  onToggle,
  testId,
}) => {
  if (clauses.length === 0) return null;

  const bgColor = isSource ? 'bg-orange-50' : 'bg-purple-50';
  const borderColor = isSource ? 'border-orange-200' : 'border-purple-200';
  const textColor = isSource ? 'text-orange-700' : 'text-purple-700';
  const iconColor = isSource ? 'text-orange-500' : 'text-purple-500';

  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg mb-4`} data-testid={testId}>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full p-3 text-left hover:bg-opacity-80 transition-colors"
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className={`h-4 w-4 ${iconColor}`} />
          ) : (
            <ChevronRight className={`h-4 w-4 ${iconColor}`} />
          )}
          {isSource ? (
            <MinusCircle className={`h-4 w-4 ${iconColor}`} />
          ) : (
            <PlusCircle className={`h-4 w-4 ${iconColor}`} />
          )}
          <span className={`font-medium text-sm ${textColor}`}>{title}</span>
        </div>
        <span className={`text-xs font-medium ${textColor} px-2 py-0.5 rounded-full ${isSource ? 'bg-orange-100' : 'bg-purple-100'}`}>
          {clauses.length}
        </span>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 max-h-64 overflow-y-auto" data-testid={`${testId}-content`}>
          {clauses.map((clause) => (
            <div
              key={clause.clauseId}
              className={`p-2 mb-2 bg-white rounded border ${borderColor}`}
              data-testid={`${testId}-clause-${clause.clauseId}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-gray-600">
                  Art. {clause.articleNumber} - Sec. {clause.sectionNumber}
                </span>
                <span className="text-xs text-gray-400">
                  Best: {Math.round(clause.bestMatchSimilarity * 100)}%
                </span>
              </div>
              <p className="text-xs text-gray-700 line-clamp-2">{clause.clauseText}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main component
export const SideBySideViewer: React.FC<SideBySideViewerProps> = ({
  sourceContract,
  targetContract,
  clauseMatches = [],
  unmatchedClauses,
  embeddingStats,
  onClauseClick,
  onEmbeddingsGenerated,
  testId = 'side-by-side-viewer',
}) => {
  const [expandedArticles, setExpandedArticles] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [showTargetUnmatched, setShowTargetUnmatched] = useState(false);
  const [isGeneratingEmbeddings, setIsGeneratingEmbeddings] = useState(false);
  const [embeddingError, setEmbeddingError] = useState<string | null>(null);
  const [sectionSearch, setSectionSearch] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [selectedTargetClauseId, setSelectedTargetClauseId] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  // Generate embeddings for contracts missing them
  const handleGenerateEmbeddings = useCallback(async () => {
    if (!sourceContract && !targetContract) return;

    setIsGeneratingEmbeddings(true);
    setEmbeddingError(null);

    try {
      const contractsToEmbed: string[] = [];

      // Check which contracts need embeddings
      if (sourceContract && embeddingStats) {
        const sourceNeedsEmbedding = embeddingStats.sourceClausesWithEmbeddings < embeddingStats.sourceClausesTotal;
        if (sourceNeedsEmbedding) {
          contractsToEmbed.push(sourceContract.id);
        }
      }

      if (targetContract && embeddingStats) {
        const targetNeedsEmbedding = embeddingStats.targetClausesWithEmbeddings < embeddingStats.targetClausesTotal;
        if (targetNeedsEmbedding) {
          contractsToEmbed.push(targetContract.id);
        }
      }

      // Generate embeddings for each contract
      for (const contractId of contractsToEmbed) {
        console.log('[SideBySideViewer] Generating embeddings for:', contractId);
        await embedClauses(contractId);
      }

      // Notify parent to refresh comparison
      if (onEmbeddingsGenerated) {
        onEmbeddingsGenerated();
      }
    } catch (error) {
      console.error('[SideBySideViewer] Error generating embeddings:', error);
      setEmbeddingError(error instanceof Error ? error.message : 'Failed to generate embeddings');
    } finally {
      setIsGeneratingEmbeddings(false);
    }
  }, [sourceContract, targetContract, embeddingStats, onEmbeddingsGenerated]);

  // Build target clause context map: targetClauseId -> { clause, section, article }
  const targetClauseContextMap = useMemo(() => {
    const map = new Map<string, TargetClauseContext>();
    targetContract?.articles.forEach((article) => {
      article.sections.forEach((section) => {
        section.clauses.forEach((clause) => {
          map.set(clause.id, { clause, section, article });
        });
      });
    });
    return map;
  }, [targetContract]);

  // Build source clause to article lookup: sourceClauseId -> Article
  const sourceClauseArticleMap = useMemo(() => {
    const map = new Map<string, Article>();
    sourceContract?.articles.forEach((article) => {
      article.sections.forEach((section) => {
        section.clauses.forEach((clause) => {
          map.set(clause.id, article);
        });
      });
    });
    return map;
  }, [sourceContract]);

  // Build source-to-target match lookup: sourceClauseId -> { targetClauseId, similarity }
  // Filters out glossary-to-non-glossary matches to avoid semantic confusion
  const sourceToTargetMatch = useMemo(() => {
    const map = new Map<string, { targetClauseId: string; similarity: number }>();
    clauseMatches.forEach((match) => {
      // Get the articles for source and target clauses
      const sourceArticle = sourceClauseArticleMap.get(match.sourceClauseId);
      const targetContext = targetClauseContextMap.get(match.targetClauseId);

      // Filter out glossary-to-non-glossary matches
      // Only match glossary items to other glossary items, and contract clauses to contract clauses
      if (sourceArticle && targetContext) {
        const sourceIsGlossary = isGlossaryArticle(sourceArticle);
        const targetIsGlossary = isGlossaryArticle(targetContext.article);

        // Skip if one is glossary and the other isn't
        if (sourceIsGlossary !== targetIsGlossary) {
          return; // Skip this match
        }
      }

      // Only keep the best match for each source clause
      const existing = map.get(match.sourceClauseId);
      if (!existing || match.similarity > existing.similarity) {
        map.set(match.sourceClauseId, {
          targetClauseId: match.targetClauseId,
          similarity: match.similarity,
        });
      }
    });
    return map;
  }, [clauseMatches, sourceClauseArticleMap, targetClauseContextMap]);

  // Build unmatched clause sets and maps
  const sourceUnmatchedIds = useMemo(
    () => new Set<string>((unmatchedClauses?.sourceUnmatched || []).map((c) => c.clauseId)),
    [unmatchedClauses]
  );
  const sourceUnmatchedMap = useMemo(
    () => new Map<string, UnmatchedClause>((unmatchedClauses?.sourceUnmatched || []).map((c) => [c.clauseId, c])),
    [unmatchedClauses]
  );

  const baselineSections = useMemo(
    () => sourceContract?.articles.flatMap((article) => article.sections.map((section) => ({ article, section }))) || [],
    [sourceContract]
  );

  useEffect(() => {
    if (!selectedSectionId && baselineSections.length > 0) {
      setSelectedSectionId(baselineSections[0].section.id);
    }
  }, [baselineSections, selectedSectionId]);

  const filteredBaselineSections = useMemo(() => {
    const query = sectionSearch.trim().toLowerCase();
    if (!query) return baselineSections;
    return baselineSections.filter(({ article, section }) => {
      const searchable = `${article.number} ${article.title} ${section.number} ${section.title}`.toLowerCase();
      return searchable.includes(query);
    });
  }, [baselineSections, sectionSearch]);

  const selectedSectionEntry = useMemo(
    () => baselineSections.find(({ section }) => section.id === selectedSectionId) || null,
    [baselineSections, selectedSectionId]
  );

  const selectedSectionClusters = useMemo<SectionAlignmentCluster[]>(() => {
    if (!selectedSectionEntry || !targetContract) return [];
    const clusters = new Map<string, SectionAlignmentCluster>();

    selectedSectionEntry.section.clauses.forEach((sourceClause) => {
      const match = sourceToTargetMatch.get(sourceClause.id);
      if (!match) return;
      const targetContext = targetClauseContextMap.get(match.targetClauseId);
      if (!targetContext) return;

      const sectionPath = `${targetContext.article.number} ${targetContext.article.title} / ${targetContext.section.number}`;
      const similarity = match.similarity;
      const label: AlignmentLabel = similarity >= 0.9
        ? 'Equivalent'
        : similarity >= 0.7
        ? 'Partial'
        : similarity >= 0.5
        ? 'Conflict'
        : 'Missing';

      if (!clusters.has(sectionPath)) {
        clusters.set(sectionPath, {
          sectionPath,
          label,
          score: similarity,
          matches: [],
        });
      }

      const cluster = clusters.get(sectionPath)!;
      cluster.score = Math.max(cluster.score, similarity);
      if (cluster.label !== 'Equivalent') {
        cluster.label = label;
      }

      cluster.matches.push({
        sourceClause,
        targetClause: targetContext.clause,
        targetSection: targetContext.section,
        targetArticle: targetContext.article,
        similarity,
        snippet: targetContext.clause.text.slice(0, 180),
        page: Number.parseInt(targetContext.clause.number.replace(/\D/g, ''), 10) || 1,
      });
    });

    const sorted = Array.from(clusters.values()).sort((a, b) => b.score - a.score);
    if (sorted.length === 0) {
      return [{ sectionPath: 'No target cluster found', label: 'Missing', score: 0, matches: [] }];
    }
    return sorted;
  }, [selectedSectionEntry, sourceToTargetMatch, targetClauseContextMap, targetContract]);

  const selectedClauseContext = useMemo(() => {
    for (const cluster of selectedSectionClusters) {
      const found = cluster.matches.find((item) => item.targetClause.id === selectedTargetClauseId);
      if (found) return found;
    }
    return selectedSectionClusters[0]?.matches[0] || null;
  }, [selectedSectionClusters, selectedTargetClauseId]);

  const handleReportIssue = useCallback(() => {
    if (!selectedSectionEntry) return;
    const payload = {
      baselineSectionId: selectedSectionEntry.section.id,
      baselineSectionTitle: selectedSectionEntry.section.title,
      selectedTargetClauseId: selectedClauseContext?.targetClause.id ?? null,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem('alignment-feedback:last', JSON.stringify(payload));
    setFeedbackMessage('Feedback captured for this alignment.');
  }, [selectedSectionEntry, selectedClauseContext]);

  // Auto-expand articles with matches on initial load
  useEffect(() => {
    if (clauseMatches.length === 0 || !sourceContract) return;

    const sourceMatchedClauseIds = new Set(clauseMatches.map((m) => m.sourceClauseId));

    const articlesWithMatches = new Set<string>();
    const sectionsWithMatches = new Set<string>();

    sourceContract.articles.forEach((article) => {
      let articleHasMatch = false;
      article.sections.forEach((section) => {
        const sectionHasMatch = section.clauses.some((clause) => sourceMatchedClauseIds.has(clause.id));
        if (sectionHasMatch) {
          sectionsWithMatches.add(section.id);
          articleHasMatch = true;
        }
      });
      if (articleHasMatch) {
        articlesWithMatches.add(article.id);
      }
    });

    setExpandedArticles(articlesWithMatches);
    setExpandedSections(sectionsWithMatches);
  }, [clauseMatches, sourceContract]);

  // Article toggle handlers
  const toggleArticle = (articleId: string) => {
    setExpandedArticles((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) {
        next.delete(articleId);
      } else {
        next.add(articleId);
      }
      return next;
    });
  };

  // Section toggle handlers
  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // Expand/Collapse all handlers
  const expandAll = () => {
    if (sourceContract) {
      const articleIds = new Set(sourceContract.articles.map((a) => a.id));
      const sectionIds = new Set(sourceContract.articles.flatMap((a) => a.sections.map((s) => s.id)));
      setExpandedArticles(articleIds);
      setExpandedSections(sectionIds);
    }
  };

  const collapseAll = () => {
    setExpandedArticles(new Set());
    setExpandedSections(new Set());
  };

  // Calculate match statistics
  const matchStats = {
    total: clauseMatches.length,
    exact: clauseMatches.filter((m) => m.similarity >= 0.9).length,
    similar: clauseMatches.filter((m) => m.similarity >= 0.7 && m.similarity < 0.9).length,
    partial: clauseMatches.filter((m) => m.similarity >= 0.5 && m.similarity < 0.7).length,
  };

  const hasUnmatchedClauses =
    (unmatchedClauses?.totalSourceUnmatched || 0) > 0 ||
    (unmatchedClauses?.totalTargetUnmatched || 0) > 0;

  // Check for embedding warning
  const showEmbeddingWarning = embeddingStats && (
    embeddingStats.sourceClausesWithEmbeddings === 0 ||
    embeddingStats.targetClausesWithEmbeddings === 0
  );

  // Empty state for missing contracts
  if (!sourceContract && !targetContract) {
    return (
      <div
        className="bg-white rounded-lg shadow-md p-8 text-center"
        data-testid={testId}
      >
        <GitCompare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Contracts Selected</h3>
        <p className="text-sm text-gray-500">
          Select a baseline and target contract to compare them side-by-side.
        </p>
      </div>
    );
  }

  if (!sourceContract) {
    return (
      <div
        className="bg-white rounded-lg shadow-md p-8 text-center"
        data-testid={testId}
      >
        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Select Baseline Contract</h3>
        <p className="text-sm text-gray-500">
          Choose a baseline contract to display its structure.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md" data-testid={testId}>
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <GitCompare className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Side-by-Side Comparison</h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={expandAll}
              className="text-xs text-primary-600 hover:text-primary-700"
              data-testid={`${testId}-expand-all`}
            >
              Expand All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={collapseAll}
              className="text-xs text-primary-600 hover:text-primary-700"
              data-testid={`${testId}-collapse-all`}
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* Contract names header */}
        <div className="flex gap-4 mt-3">
          <div className="flex-1 flex items-center">
            <span className="w-3 h-3 rounded-full bg-primary-500 mr-2" />
            <span className="font-medium text-gray-900">{sourceContract.name}</span>
            <span className="ml-2 text-sm text-gray-500">({sourceContract.state})</span>
            <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs">Baseline</span>
          </div>
          <div className="w-8" /> {/* Spacer for arrow column */}
          <div className="flex-1 flex items-center">
            <span className="w-3 h-3 rounded-full bg-secondary-500 mr-2" />
            {targetContract ? (
              <>
                <span className="font-medium text-gray-900">{targetContract.name}</span>
                <span className="ml-2 text-sm text-gray-500">({targetContract.state})</span>
                <span className="ml-2 px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded text-xs">Target</span>
              </>
            ) : (
              <span className="text-gray-400">No target contract selected</span>
            )}
          </div>
        </div>

        {/* Embedding warning */}
        {showEmbeddingWarning && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`${testId}-embedding-warning`}>
            <div className="flex items-start gap-2">
              <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Limited Matching Results</p>
                <p className="text-xs text-amber-700 mt-1">
                  {embeddingStats?.warning ||
                    `One or more contracts have clauses without embeddings. ` +
                    `Source: ${embeddingStats?.sourceClausesWithEmbeddings}/${embeddingStats?.sourceClausesTotal} embedded. ` +
                    `Target: ${embeddingStats?.targetClausesWithEmbeddings}/${embeddingStats?.targetClausesTotal} embedded.`}
                </p>
                {embeddingError && (
                  <p className="text-xs text-red-600 mt-1">Error: {embeddingError}</p>
                )}
              </div>
              <button
                onClick={handleGenerateEmbeddings}
                disabled={isGeneratingEmbeddings}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`${testId}-generate-embeddings-btn`}
              >
                {isGeneratingEmbeddings ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5" />
                    Generate Embeddings
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Match statistics */}
        {clauseMatches.length > 0 && (
          <div className="mt-3 flex items-center gap-4" data-testid={`${testId}-match-stats`}>
            <span className="text-sm text-gray-500">Matched clauses:</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              {matchStats.exact} exact (90%+)
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              {matchStats.similar} similar (70-89%)
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              {matchStats.partial} partial (50-69%)
            </span>
          </div>
        )}

        {/* No matches warning */}
        {clauseMatches.length === 0 && targetContract && (
          <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid={`${testId}-no-matches`}>
            <p className="text-sm text-gray-600">
              No matching clauses found between these contracts. This could indicate:
            </p>
            <ul className="mt-1 text-xs text-gray-500 list-disc list-inside">
              <li>Contracts have very different content or structure</li>
              <li>Embeddings may not have been generated for all clauses</li>
              <li>The similarity threshold may be too high</li>
            </ul>
          </div>
        )}

        {/* Unmatched clauses stats */}
        {hasUnmatchedClauses && (
          <div className="mt-3 flex items-center gap-4" data-testid={`${testId}-unmatched-stats`}>
            <span className="text-sm text-gray-500">Unmatched:</span>
            {(unmatchedClauses?.totalSourceUnmatched || 0) > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                <MinusCircle className="h-3 w-3 mr-1" />
                {unmatchedClauses?.totalSourceUnmatched} baseline clauses without target match
              </span>
            )}
            {(unmatchedClauses?.totalTargetUnmatched || 0) > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                <PlusCircle className="h-3 w-3 mr-1" />
                {unmatchedClauses?.totalTargetUnmatched} extra clauses in target
              </span>
            )}
          </div>
        )}

        {/* Legend */}
        <div className="mt-3 flex flex-wrap items-center gap-4" data-testid={`${testId}-legend`}>
          <span className="text-sm text-gray-500">Legend:</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-100 border-2 border-green-400" />
            <span className="text-xs text-gray-600">90%+ match</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-blue-100 border-2 border-blue-400" />
            <span className="text-xs text-gray-600">70-89%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-yellow-100 border-2 border-yellow-400" />
            <span className="text-xs text-gray-600">50-69%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-orange-50 border-2 border-orange-400" />
            <span className="text-xs text-gray-600">No match in target</span>
          </div>
        </div>
      </div>

      {/* Extra target clauses panel (clauses in target that have no source match) */}
      {unmatchedClauses && unmatchedClauses.targetUnmatched.length > 0 && (
        <div className="px-4 pt-4" data-testid={`${testId}-target-unmatched-panel`}>
          <UnmatchedClausesPanel
            title="Extra clauses in target contract (not in baseline)"
            clauses={unmatchedClauses.targetUnmatched}
            isSource={false}
            isExpanded={showTargetUnmatched}
            onToggle={() => setShowTargetUnmatched(!showTargetUnmatched)}
            testId={`${testId}-target-unmatched`}
          />
        </div>
      )}

      {/* Main content - alignment-first compare */}
      <div className="p-4" data-testid={`${testId}-content`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 border border-gray-200 rounded-lg" data-testid={`${testId}-baseline-toc`}>
            <div className="p-3 border-b border-gray-200">
              <p className="text-sm font-semibold text-gray-900">Baseline TOC</p>
              <p className="text-xs text-gray-500 truncate">{sourceContract.name} · {sourceContract.id.slice(0, 8)}</p>
              <input
                type="search"
                className="mt-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="Search baseline sections"
                value={sectionSearch}
                onChange={(event) => setSectionSearch(event.target.value)}
                data-testid={`${testId}-baseline-search`}
              />
            </div>
            <div className="max-h-[600px] overflow-y-auto p-2 space-y-1">
              {filteredBaselineSections.map(({ article, section }) => {
                const isSelected = section.id === selectedSectionId;
                return (
                  <button
                    key={section.id}
                    className={`w-full text-left rounded px-2 py-2 text-sm border ${
                      isSelected
                        ? 'bg-primary-50 border-primary-300 text-primary-900'
                        : 'bg-white border-transparent hover:border-gray-200'
                    }`}
                    onClick={() => setSelectedSectionId(section.id)}
                    data-testid={`${testId}-toc-section-${section.id}`}
                  >
                    <p className="text-xs text-gray-500">{article.number}</p>
                    <p className="font-medium">{section.number} {section.title}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2 border border-gray-200 rounded-lg" data-testid={`${testId}-match-clusters`}>
            <div className="p-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">Target Match Clusters</p>
                <p className="text-xs text-gray-500 truncate">{targetContract?.name} · {targetContract?.id.slice(0, 8)}</p>
                <p className="text-xs text-gray-500">
                  {selectedSectionEntry
                    ? `${selectedSectionEntry.section.number} ${selectedSectionEntry.section.title}`
                    : 'Select a section to review alignments'}
                </p>
              </div>
              <button
                type="button"
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                onClick={handleReportIssue}
                data-testid={`${testId}-report-issue`}
              >
                Report an issue
              </button>
            </div>
            <div className="p-3 space-y-3">
              {selectedSectionClusters.map((cluster, index) => (
                <div key={`${cluster.sectionPath}-${index}`} className="border border-gray-200 rounded p-3" data-testid={`${testId}-cluster-${index}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{cluster.sectionPath}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{(cluster.score * 100).toFixed(1)}%</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{cluster.label}</span>
                    </div>
                  </div>
                  <div className="mt-2 space-y-2">
                    {cluster.matches.map((match) => (
                      <button
                        key={match.targetClause.id}
                        className="w-full text-left rounded border border-gray-200 px-2 py-2 hover:bg-gray-50"
                        onClick={() => setSelectedTargetClauseId(match.targetClause.id)}
                        data-testid={`${testId}-cluster-match-${match.targetClause.id}`}
                      >
                        <p className="text-xs text-gray-500">{match.targetSection.number} • p.{match.page}</p>
                        <p className="text-sm text-gray-700">{match.snippet}...</p>
                      </button>
                    ))}
                    {cluster.matches.length === 0 && (
                      <p className="text-sm text-gray-500" data-testid={`${testId}-cluster-missing`}>No target evidence for this section.</p>
                    )}
                  </div>
                </div>
              ))}

              {selectedClauseContext && (
                <div className="border border-gray-200 rounded p-3 bg-gray-50" data-testid={`${testId}-citation-viewer`}>
                  <p className="text-sm font-semibold text-gray-900">Clause citation viewer</p>
                  <p className="text-xs text-gray-500 mt-1">Target {selectedClauseContext.targetSection.number} • page {selectedClauseContext.page}</p>
                  <p className="text-sm mt-2 text-gray-700">{selectedClauseContext.targetClause.text}</p>
                </div>
              )}

              {feedbackMessage && (
                <p className="text-xs text-green-700" data-testid={`${testId}-feedback-message`}>{feedbackMessage}</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-gray-200 pt-4">
          {sourceContract.articles.map((article) => (
            <ArticleGroup
              key={article.id}
              article={article}
              sourceContractId={sourceContract.id}
              targetContractId={targetContract?.id}
              sourceToTargetMatch={sourceToTargetMatch}
              targetClauseContextMap={targetClauseContextMap}
              unmatchedClauseIds={sourceUnmatchedIds}
              unmatchedClausesMap={sourceUnmatchedMap}
              isExpanded={expandedArticles.has(article.id)}
              expandedSections={expandedSections}
              onToggle={() => toggleArticle(article.id)}
              onSectionToggle={toggleSection}
              onClauseClick={onClauseClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SideBySideViewer;
